import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError } from '@/api/errors';
import { meetingsApi, type MeetingHome, type MeetingNote } from '@/api/meetings';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HeadedCard } from '@/components/HeadedCard';
import { useConfirm } from '@/components/useConfirm';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { excerpt } from '@/utils/excerpt';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { sameValues } from '@/shared/useDirty';
import { useUnsavedGuard } from '@/shared/useUnsavedGuard';

interface SessionNotesProps {
  /** The meeting's home; a standalone meeting's (null) notes are edited, never deleted. */
  virtualAccountId: MeetingHome;
  meetingId: number;
  occurrenceKey: string;
  notes: MeetingNote[];
  canWrite: boolean;
  onChanged: () => void;
}

export function SessionNotes({
  virtualAccountId,
  meetingId,
  occurrenceKey,
  notes,
  canWrite,
  onChanged,
}: SessionNotesProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');
  const [conflict, setConflict] = useState(false);
  // After Reload the saved text is shown above the draft, so the person sees "their changes" and
  // decides; Save then carries the fresh `version` over a text they have seen (ANTI-019).
  const [reloaded, setReloaded] = useState(false);
  const deleting = useConfirm<MeetingNote>();
  // Unsaved work: a note being written, or an inline edit that differs from the saved note.
  const editingNote = notes.find((note) => note.id === editingId);
  const dirty =
    !sameValues(draft, '') ||
    (editingNote !== undefined && !sameValues(editBody, editingNote.body));
  // The notes live in a tab of the session page, so Back (and a tab switch) is a query-only move that
  // would unmount the editor with the draft in it: hold it like a dialog does (TMMAPP-0082).
  const guard = useUnsavedGuard(dirty, { includeSearch: true });

  async function handleAdd() {
    if (draft.trim().length === 0) return;
    await meetingsApi.addNote(virtualAccountId, meetingId, occurrenceKey, draft.trim());
    setDraft('');
    onChanged();
  }

  async function handleSave(note: MeetingNote) {
    try {
      await meetingsApi.patchNote(virtualAccountId, meetingId, occurrenceKey, note.id, {
        body: editBody,
        version: note.version,
      });
      setEditingId(null);
      setConflict(false);
      setReloaded(false);
      onChanged();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.NOTE_VERSION_CONFLICT) setConflict(true);
    }
  }

  return (
    <HeadedCard
      title={t('workspace.notes')}
      subtitle={t('meetings.session.notesSub')}
      id="session-notes"
    >
      <Stack spacing={2}>
        {notes.map((note) => {
          const mine = me.user.id !== null && note.authorUserId === me.user.id;
          return (
            <Box key={note.id}>
              <Typography variant="caption" color="text.secondary">
                {[
                  note.authorDisplayName,
                  note.createdAt ? formatInstant(note.createdAt, locale, zone) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              {editingId === note.id ? (
                <Stack spacing={1}>
                  {conflict && (
                    <Alert
                      severity="warning"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => {
                            onChanged();
                            setConflict(false);
                            setReloaded(true);
                          }}
                        >
                          {t('common.reload')}
                        </Button>
                      }
                    >
                      {t('meetings.noteConflict')}
                    </Alert>
                  )}
                  {reloaded && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('meetings.noteLatest')}
                      </Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{note.body}</Typography>
                    </Box>
                  )}
                  <TextField
                    value={editBody}
                    onChange={(event) => setEditBody(event.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <Button size="small" onClick={() => void handleSave(note)}>
                    {t('common.save')}
                  </Button>
                </Stack>
              ) : (
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{note.body}</Typography>
              )}
              {canWrite && mine && (
                <Stack direction="row" sx={{ gap: 1 }}>
                  {editingId !== note.id && (
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingId(note.id);
                        setEditBody(note.body);
                        setConflict(false);
                        setReloaded(false);
                      }}
                    >
                      {t('meetings.edit')}
                    </Button>
                  )}
                  {virtualAccountId !== null && (
                    <Button size="small" color="error" onClick={() => deleting.ask(note)}>
                      {t('meetings.deleteNote')}
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      <UnsavedChangesDialog guard={guard} />
      {deleting.target !== null && virtualAccountId !== null && (
        <ConfirmDialog
          title={t('meetings.deleteNoteTitle', { excerpt: excerpt(deleting.target.body) })}
          consequence={t('meetings.deleteNoteConsequence')}
          confirmLabel={t('meetings.deleteNote')}
          destructive
          busy={deleting.busy}
          error={deleting.error}
          onConfirm={() =>
            void deleting.run(
              async (note) => {
                await meetingsApi.deleteNote(virtualAccountId, meetingId, occurrenceKey, note.id);
                onChanged();
              },
              () => t('meetings.errorGeneric'),
            )
          }
          onCancel={deleting.cancel}
        />
      )}
      {canWrite && (
        <Stack sx={{ mt: 2 }} spacing={1}>
          <TextField
            label={t('meetings.noteBody')}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            multiline
            minRows={2}
          />
          <Button
            variant="contained"
            onClick={() => void handleAdd()}
            disabled={draft.trim().length === 0}
          >
            {t('meetings.addNote')}
          </Button>
        </Stack>
      )}
    </HeadedCard>
  );
}
